import {
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  HomeWorkOutlined as HomeWorkOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
} from '@mui/icons-material';
import { Box, Collapse, Dialog, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { UserAvatar } from '@/components/common/UserAvatar';
import { CloseButton } from '@/components/ds';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserPresence } from '@/hooks/user/useUserPresence';
import { useUserRole } from '@/hooks/useUserRole';
import { getPublicUserProfile } from '@/services/api/users/api';

// ─── Engagement period helpers ──────────────────────────────────────────────
const PERIOD_DAYS_MAP: Record<string, number> = {
  Initial: 90,
  'Ext I': 90,
  'Ext II': 90,
  'Ext III': 90,
};
const URGENCY_COLORS = {
  low: '#5AE9FF',
  moderate: '#FFF981',
  urgent: '#FF9A4D',
  critical: '#DB5282',
};

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

function getDealEngagementInfo(engagementStart: string, periodType: string) {
  if (!engagementStart) return null;
  const start = new Date(engagementStart + 'T00:00:00');
  const totalDays = PERIOD_DAYS_MAP[periodType] || 90;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const elapsed = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const days = Math.min(totalDays - Math.max(0, elapsed), totalDays);
  const progress = Math.max(0, Math.min(1, elapsed / totalDays));

  let color: string;
  if (days > 60) color = URGENCY_COLORS.low;
  else if (days > 30) color = URGENCY_COLORS.moderate;
  else if (days > 14) color = URGENCY_COLORS.urgent;
  else color = URGENCY_COLORS.critical;

  return { days: Math.max(0, days), progress, color, expired: days <= 0 };
}

/**
 * Shows the "other side" of the property relationship in the sidebar.
 * Advisors see their clients (Viewers); buyers see their advisors (Owner/Editor).
 */
type PropertyAdvisorsProps = {
  showIndependentOwnerPrompt?: boolean;
  promptOnly?: boolean;
  /** Inside the sidebar's context tile: deal and time left on the person's second line, engagement as the tile's
   *  bottom edge */
  inTile?: boolean;
};

export const PropertyAdvisors = ({
  showIndependentOwnerPrompt: allowIndependentOwnerPrompt = true,
  promptOnly = false,
  inTile = false,
}: PropertyAdvisorsProps = {}) => {
  const [expanded, setExpanded] = useState(false);
  const [nextMoveOpen, setNextMoveOpen] = useState(false);
  const [nextMoveCenter, setNextMoveCenter] = useState({ x: 0, y: 0 });
  const isMobile = useWindowWidth() <= 768;
  const { selectedProperty } = usePropertiesContext();
  const { isAdvisor } = useUserRole();
  const { groups, isFetching: groupsFetching, refetch: refetchGroups } = useClientGroups();

  const pid = selectedProperty?.property.id;
  const propertyGroupIds = groups
    .filter(
      (g) =>
        (g.propertyIds ?? []).includes(pid ?? '') ||
        (selectedProperty?.clientGroupIds ?? []).includes(g.id),
    )
    .map((g) => g.id);

  // When the selected property has known group associations but the loaded groups don't
  // reflect them yet (stale cache), force a refetch to resolve the race condition.
  // Guard on !groupsFetching to avoid triggering a second request while one is in-flight.
  const knownGroupIds = selectedProperty?.clientGroupIds ?? [];
  const groupsAreMissingAssociations =
    knownGroupIds.length > 0 && propertyGroupIds.length === 0 && !groupsFetching;
  useEffect(() => {
    if (groupsAreMissingAssociations) {
      refetchGroups();
    }
  }, [groupsAreMissingAssociations, refetchGroups]);

  useEffect(() => {
    if (!nextMoveOpen) return;
    const mainContent = document.getElementById('dashboard-main-content');
    if (!mainContent) return;

    let frame = 0;
    const updateCenter = () => {
      const mainRect = mainContent.getBoundingClientRect();
      const x = mainRect.left + mainRect.width / 2;
      const y = mainRect.top + mainRect.height / 2;
      setNextMoveCenter((current) =>
        Math.abs(current.x - x) < 0.5 && Math.abs(current.y - y) < 0.5 ? current : { x, y },
      );
      frame = requestAnimationFrame(updateCenter);
    };
    updateCenter();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [nextMoveOpen]);

  // Get engagement countdown from the property/deal relationship. This is
  // intentionally not tied to whether the client has activated an account.
  const getPropertyEngagement = () => {
    if (!isAdvisor) return null;
    const propertyGroups = groups.filter(
      (g) => g.status === 'active' && propertyGroupIds.includes(g.id),
    );
    const matchedDeal = pid
      ? propertyGroups
          .flatMap((g) => g.deals)
          .find((d) => d.status !== 'Closed' && d.listings?.some((l) => l.propertyId === pid))
      : undefined;
    if (matchedDeal) {
      const info = getDealEngagementInfo(matchedDeal.engagementStart, matchedDeal.periodType);
      return info ? { ...info, dealName: matchedDeal.name } : null;
    }
    let best: (NonNullable<ReturnType<typeof getDealEngagementInfo>> & { dealName?: string }) | null = null;
    for (const group of propertyGroups) {
      for (const deal of group.deals) {
        if (deal.status === 'Closed') continue;
        const info = getDealEngagementInfo(deal.engagementStart, deal.periodType);
        if (info && (!best || info.days < best.days)) best = { ...info, dealName: deal.name };
      }
    }
    return best;
  };

  // For buyers: fetch the advisor's public profile (photo, name) via the group's advisorId
  const buyerGroup = !isAdvisor ? groups[0] : undefined;
  const advisorUserId = buyerGroup?.advisorId;
  const { data: advisorProfile } = useQuery({
    queryKey: ['public-profile', advisorUserId],
    queryFn: () => getPublicUserProfile(advisorUserId as string),
    enabled: Boolean(advisorUserId),
    staleTime: 5 * 60 * 1000,
  });

  // Advisors see clients sourced from advisor-client groups linked to this property.
  // Buyers see their advisor (single entry derived from their group's advisorId).
  const people: { userId?: string; userName: string; photoUrl?: string }[] = isAdvisor
    ? (propertyGroupIds.length > 0
        ? groups.filter((g) => propertyGroupIds.includes(g.id) && g.status === 'active')
        : []
      ).flatMap((g) =>
        g.members.map((m) => ({ userId: m.userId, userName: m.name, photoUrl: m.photoUrl })),
      )
    : advisorUserId
    ? [
        {
          userId: advisorUserId,
          userName: advisorProfile?.name || 'Advisor',
          photoUrl: advisorProfile?.photoUrl,
        },
      ]
    : [];
  const roleLabel = isAdvisor ? 'Client' : 'Advisor';

  // Deduplicate by userId to avoid showing the same person multiple times
  const seen = new Set<string>();
  const uniquePeople = people.filter((p) => {
    const key = p.userId || p.userName;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const peopleUserIds = uniquePeople.map((p) => p.userId).filter(Boolean) as string[];
  const { getLastActiveAt } = useUserPresence(peopleUserIds);

  if (!selectedProperty) return null;

  if (uniquePeople.length === 0) {
    const emptyLabel = isAdvisor ? 'No client assigned' : 'No advisor assigned';
    const shouldShowIndependentOwnerPrompt =
      !isAdvisor && groups.length === 0 && selectedProperty.isOwned === true;

    if (allowIndependentOwnerPrompt && shouldShowIndependentOwnerPrompt) {
      return (
        <>
          <Box
            onClick={() => setNextMoveOpen(true)}
            sx={{
              mx: 0,
              my: '10px',
              pl: '12px',
              borderLeft: '1px solid var(--tx-line)',
              cursor: 'pointer',
              '&:hover .next-move-copy': { color: 'var(--tx-text)' },
              '&:hover .next-move-arrow': {
                color: 'var(--tx-lime-ink)',
                transform: 'translateX(4px)',
              },
            }}>
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '.14em',
                color: '#77736e',
                mb: '5px',
              }}>
              PLANNING YOUR NEXT MOVE?
            </Typography>
            <Typography
              className="next-move-copy"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: 11,
                lineHeight: 1.5,
                color: 'var(--tx-faint)',
                transition: 'color 150ms ease',
              }}>
              Explore buying or selling with the right team.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '7px' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-muted)' }}>
                Get started
              </Typography>
              <ArrowForwardIcon
                className="next-move-arrow"
                sx={{ fontSize: 13, color: 'var(--tx-muted)', transition: 'all 160ms ease' }}
              />
            </Box>
          </Box>

          <Dialog
            open={nextMoveOpen}
            onClose={() => setNextMoveOpen(false)}
            maxWidth={false}
            slotProps={{
              paper: {
                sx: {
                  width: isMobile ? 'calc(100vw - 32px)' : 650,
                  height: isMobile ? 'auto' : 520,
                  maxHeight: isMobile ? 'calc(100dvh - 32px)' : 'none',
                  maxWidth: 'none',
                  boxSizing: 'border-box',
                  m: 0,
                  position: 'fixed',
                  left: isMobile ? '50vw' : nextMoveCenter.x || '50%',
                  top: isMobile ? '50dvh' : nextMoveCenter.y || '50%',
                  transform: 'translate(-50%, -50%)',
                  bgcolor: 'var(--tx-glass-panel)',
                  backgroundImage: 'none',
                  border: 0,
                  borderRadius: '25px',
                  boxShadow: '0 28px 80px rgba(0,0,0,.55)',
                  p: '25px',
                  overflowY: isMobile ? 'auto' : 'visible',
                },
              },
              backdrop: {
                sx: { bgcolor: 'rgba(0,0,0,.46)', backdropFilter: 'blur(6px)' },
              },
            }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}>
              {/* inner box sits inside the paper's 25px padding; -7 puts the X at the 18px sheet inset */}
              <CloseButton
                onClose={() => setNextMoveOpen(false)}
                label="Close"
                sx={{ position: 'absolute', top: -7, right: -7 }}
              />

              <Typography sx={{ fontSize: 24, fontWeight: 300, letterSpacing: '-0.02em', color: 'var(--tx-text)', mb: '7px' }}>
                What’s your next move?
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#918d87', mb: '28px' }}>
                Choose the path that fits where you’re headed. We’ll connect you with the right team.
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '14px',
                  flex: isMobile ? 'initial' : 1,
                  minHeight: 0,
                }}>
                {[
                  {
                    brand: 'AVENIDA',
                    title: 'Sell a home',
                    body: 'Understand timing, positioning, and your clearest path to market.',
                    action: 'Explore selling',
                    url: 'https://avenidaliving.com',
                    Icon: HomeWorkOutlinedIcon,
                  },
                  {
                    brand: 'HAUSER',
                    title: 'Buy a home',
                    body: 'Evaluate homes clearly and move forward with greater confidence.',
                    action: 'Explore buying',
                    url: 'https://hauserhq.com',
                    Icon: KeyOutlinedIcon,
                  },
                ].map(({ brand, title, body, action, url, Icon }) => (
                  <Box
                    key={brand}
                    component="button"
                    type="button"
                    onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    sx={{
                      appearance: 'none',
                      textAlign: 'left',
                      bgcolor: 'var(--tx-fill)',
                      border: 0,
                      borderRadius: '16px',
                      p: isMobile ? '18px' : '22px',
                      color: 'inherit',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 160ms ease, background 160ms ease',
                      '&:hover': {
                        bgcolor: 'var(--tx-fill-2)',
                        transform: 'translateY(-2px)',
                      },
                      '&:hover .path-arrow': { transform: 'translateX(4px)', color: '#c0ff02' },
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '.14em', color: '#918d87' }}>
                        {brand}
                      </Typography>
                      <Icon sx={{ fontSize: 22, color: 'var(--tx-muted)' }} />
                    </Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 600, color: 'var(--tx-text)', mt: isMobile ? '22px' : '34px' }}>
                      {title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: '#918d87', mt: '7px', minHeight: 40 }}>
                      {body}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: isMobile ? '22px' : 'auto' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>
                        {action}
                      </Typography>
                      <ArrowForwardIcon
                        className="path-arrow"
                        sx={{ fontSize: 15, color: 'var(--tx-muted)', transition: 'all 160ms ease' }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                component="button"
                type="button"
                sx={{
                  mt: '16px',
                  p: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  color: '#77736e',
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 11,
                  cursor: 'pointer',
                  '&:hover': { color: 'var(--tx-muted)' },
                }}>
                I’m considering both / just exploring
              </Box>
            </Box>
          </Dialog>
        </>
      );
    }

    if (promptOnly) return null;

    return (
      <Box sx={{ px: '12px', py: '10px' }}>
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: 13,
            color: 'var(--tx-faint)',
            fontStyle: 'italic',
          }}>
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  const firstPerson = uniquePeople[0];
  const remainingPeople = uniquePeople.slice(1);
  const propertyEngagement = getPropertyEngagement();

  return (
    <Box sx={{ px: 0, pb: inTile ? 0 : '4px' }}>
      {/* First person */}
      <Box
        onClick={() => remainingPeople.length > 0 && setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '10px',
          // in the tile: even space above (to the hairline) and below (to the edge line, with the tile's 6px)
          pt: inTile ? '11px' : '8px',
          pb: inTile ? '5px' : '8px',
          borderRadius: '12px',
          cursor: remainingPeople.length > 0 ? 'pointer' : 'default',
          '&:hover': remainingPeople.length > 0 ? { backgroundColor: 'var(--tx-fill)' } : {},
        }}>
        <UserAvatar
          name={firstPerson.userName}
          photoUrl={firstPerson.photoUrl}
          size={inTile ? 28 : 32}
          lastActiveAt={firstPerson.userId ? getLastActiveAt(firstPerson.userId) : undefined}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Typography
            noWrap
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--tx-text)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
            }}>
            {firstPerson.userName}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--tx-muted)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {inTile && propertyEngagement ? (
              <>
                {propertyEngagement.dealName ?? roleLabel} ·{' '}
                <span style={{ color: propertyEngagement.color, fontWeight: 600 }}>
                  {propertyEngagement.expired ? 'Expired' : `${propertyEngagement.days}d left`}
                </span>
              </>
            ) : (
              roleLabel
            )}
          </Typography>
        </Box>
        {remainingPeople.length > 0 &&
          (expanded ? (
            <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
          ) : (
            <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
          ))}
      </Box>
      {/* Engagement countdown — full width under avatar row */}
      {(() => {
        const eng = propertyEngagement;
        if (!eng) return null;
        if (inTile)
          return (
            // the tile's bottom edge is the engagement line: elapsed time in its urgency colour over a quiet track
            <Box
              aria-label={eng.expired ? 'Engagement expired' : `Engagement: ${eng.days} days left`}
              sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, backgroundColor: 'var(--tx-fill-2)' }}>
              <Box sx={{ width: `${eng.progress * 100}%`, height: '100%', backgroundColor: eng.color, transition: 'width 0.4s ease' }} />
            </Box>
          );
        return (
          <Box sx={{ px: '10px', pt: '4px', pb: '8px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: '3px',
              }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--tx-muted)',
                }}>
                Engagement
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: 10,
                  fontWeight: 600,
                  color: eng.color,
                  letterSpacing: '0.01em',
                }}>
                {eng.expired ? 'Expired' : `${eng.days}d`}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 2,
                borderRadius: 1,
                backgroundColor: 'var(--tx-fill-2)',
                overflow: 'hidden',
              }}>
              <Box
                sx={{
                  width: `${eng.progress * 100}%`,
                  height: '100%',
                  borderRadius: 2,
                  backgroundColor: eng.color,
                  transition: 'width 0.4s ease',
                }}
              />
            </Box>
          </Box>
        );
      })()}

      {/* Remaining people */}
      <Collapse in={expanded}>
        {remainingPeople.map((person, i) => {
          const eng = propertyEngagement;
          return (
            <Box key={person.userId || `member-${i}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  px: '12px',
                  py: '10px',
                  mt: 0.5,
                  borderRadius: '8px',
                }}>
                <UserAvatar
                  name={person.userName}
                  photoUrl={person.photoUrl}
                  size={40}
                  lastActiveAt={person.userId ? getLastActiveAt(person.userId) : undefined}
                />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                  }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'var(--tx-text)',
                      lineHeight: 1,
                      letterSpacing: '-0.01em',
                    }}>
                    {person.userName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: 12,
                      color: 'var(--tx-muted)',
                      lineHeight: 1,
                    }}>
                    {roleLabel}
                  </Typography>
                </Box>
              </Box>
              {eng && (
                <Box sx={{ px: '10px', pt: '4px', pb: '8px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: '3px',
                    }}>
                    <Typography
                      sx={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--tx-faint)',
                      }}>
                      Engagement
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: 10,
                        fontWeight: 600,
                        color: eng.color,
                        letterSpacing: '0.01em',
                      }}>
                      {eng.expired ? 'Expired' : `${eng.days}d`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 3,
                      borderRadius: 2,
                      backgroundColor: 'var(--tx-fill-2)',
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        width: `${eng.progress * 100}%`,
                        height: '100%',
                        borderRadius: 2,
                        backgroundColor: eng.color,
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
};
